import React, { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { showToast } from '../../../../helper/ShowToast';

export default function URLActionBar({ fullLink, linkType, domain, shortId, onChangeShortId, isPreview = false, canEdit = true }) {
    const [copied, setCopied] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const initialShortIdRef = useRef(shortId);
    const [localShortId, setLocalShortId] = useState(shortId);

    useEffect(() => {
        setLocalShortId(shortId);
        initialShortIdRef.current = shortId;
    }, [shortId]);

    useEffect(() => {
        if (!canEdit) setIsEditing(false);
    }, [canEdit]);

    const handleCopy = () => {
        try {
            navigator.clipboard.writeText(fullLink);
            showToast('success', "Link copied!")
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            showToast('error', "Failed to copy link");
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: "Check this out",
            text: "Have a look at this link",
            url: fullLink,
        };

        // Try native share first (mobile + some desktop browsers)
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (err) {
                // user cancelled → silently ignore
                console.log("Share cancelled");
            }
        }

        //custom platform selection
        const encodedUrl = encodeURIComponent(fullLink);
        const encodedText = encodeURIComponent("Check this out");

        const platforms = [
            {
                name: "WhatsApp",
                url: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
            },
            {
                name: "Email",
                url: `mailto:?subject=${encodedText}&body=${encodedUrl}`,
            },
            {
                name: "Facebook",
                url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            },
            {
                name: "Twitter / X",
                url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
            },
            {
                name: "LinkedIn",
                url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            },
        ];
        const choice = window.prompt(
            "Share via:\n" +
            platforms.map((p, i) => `${i + 1}. ${p.name}`).join("\n")
        );
        const selected = platforms[Number(choice) - 1];
        if (selected) {
            window.open(selected.url, "_blank", "noopener,noreferrer");
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const handleBlur = () => {
        setIsEditing(false);
        onChangeShortId?.(localShortId);
    };

    const handleChange = (e) => {
        let value = e.target.value;
        if (value.includes("http")) {
            showToast("warning", "Only edit short link, not domain");
            value = value.replace(/^https?:\/\/[^/]+\//, "");
        }

        value = value.toLowerCase().replace(/[^a-z0-9-]/g, "-");

        setLocalShortId(value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            setIsEditing(false);
            onChangeShortId?.(localShortId);
        }

        if (e.key === "Escape") {
            setLocalShortId(initialShortIdRef.current);
            setIsEditing(false);
        }
    };
    const isEditableLink = ["biolink", "smartlink", "tourlink", "presave"].includes(linkType);

    return (
        <div className="bg-[#191E8B] rounded-lg shadow-lg px-3 py-2 flex flex-nowrap items-center justify-between gap-3 min-w-0">
            {/* URL Display */}
            {
                isEditableLink ?
                    <div className="flex-1 min-w-0 flex items-center text-white text-sm font-medium overflow-hidden">
                        <span className="opacity-80">{domain}</span>
                        {(localShortId || isEditing) && (
                            <span className="opacity-80">/</span>
                        )}
                        {isEditing ? (
                            <input
                                ref={inputRef}
                                value={localShortId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                className="bg-indigo-800 ml-1 px-2 py-1 rounded outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        ) : (
                            <span className="ml-1 truncate">{localShortId}</span>
                        )}
                    </div>
                    :
                    <div className="flex-1 min-w-0">
                        <span className="text-white text-xs sm:text-sm font-medium block truncate">{fullLink}</span>
                    </div>
            }
            {/* Action Buttons */}
            <div className="flex flex-nowrap items-center gap-2 sm:gap-3 shrink-0 text-xs sm:text-sm">
                {!isPreview && (
                    <button className="flex items-center gap-2 text-theme_clr2 hover:text-orange-300 transition-colors font-medium text-xs sm:text-sm"
                        onClick={handleShare}
                    >
                        <span>Share</span>
                    </button>
                )}
                {!isPreview && (
                    <a href={fullLink} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-theme_clr2 hover:text-orange-300 transition-colors font-medium text-xs sm:text-sm">
                        <span>View</span>
                    </a>
                )}
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 text-theme_clr2 hover:text-orange-300 transition-colors font-medium text-xs sm:text-sm"
                >
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
                {
                    isEditableLink && canEdit ?
                        <button className="text-theme_clr2 hover:text-orange-300 transition-colors ml-1" onClick={handleEdit}>
                            <Pencil className="w-4 h-4" />
                        </button> : ''
                }
            </div>
        </div>
    );
}